import React from "react";
import { Hash, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Localizer } from "@/main/react/Types";
import { useSearchSuggestions } from "@/main/react/header/useSearchSuggestions";
import { buildSuggestionItems, type SearchSuggestionItem } from "@/main/react/header/searchSuggestionItems";

/**
 * How many chips one row may carry. The shelf is an OFFER, not a second result list — the page below
 * it already holds six filler cards, and a shelf long enough to scroll would be competing with them
 * instead of leading them. The suggestion endpoint returns a small grouped preview anyway; this is the
 * ceiling, not the expectation.
 */
const MAX_SHELF_CHIPS = 6;

export type SearchZeroMatchShelfOptions = {
    /**
     * True on the `groups` vertical only.
     *
     * <p>`DISCOVERY-86` / the wave-19 re-adjudication §1.1: *a failed `/groups` search offers groups,
     * never a post*. A group search that finds no groups is the one moment where the shelf's answer has
     * to be the same SHAPE as the question, so that vertical gets the response's group rows in addition
     * to its topics. Every other vertical stays topics-only, which is the ranking the typeahead already
     * argues for in `searchSuggestionItems.ts` ("a tag is a whole shelf of posts").
     */
    includeGroups?: boolean;
};

/**
 * The header typeahead's topic/group shelf, on the zero-match search page (`DISCOVERY-72`).
 *
 * <p><b>Why this exists.</b> `useSearchSuggestions` + `buildSuggestionItems` already resolve a query
 * into tags, discussions, groups and users, and the header's own code states the ranking rationale:
 * *"Topics come first deliberately. A tag is a whole shelf of posts, so when someone types 'climate' it
 * is usually a better answer than any single post that happens to mention it."* That reasoning is at
 * its strongest exactly when **no single post matched** — and that was the one moment the product
 * stopped asking. One keystroke earlier the dropdown offered the shelf; on the results page it
 * vanished, and the whole inventory became six recency-ranked filler cards plus an authorship CTA that
 * costs an anonymous stranger an account.
 *
 * <p><b>It must never offer a dead shelf</b>, which is the hard constraint the register row names: a
 * tag with no posts behind it is a second failed page. So nothing here is constructed from the query
 * string. The chips are built from a RESOLVED response only — `status === "ready"` **and**
 * `responseQuery` equal to this page's normalized query — which is precisely the staleness check
 * `useSearchSuggestions` exposes `responseQuery` for. The hook deliberately retains the previous
 * response while a refined query loads (anti-flicker), so without that equality a shelf could point at
 * topics belonging to a query the visitor has already left behind. Rows whose target does not resolve
 * are dropped by `buildSuggestionItems` before they reach here.
 *
 * <p><b>The request is usually free.</b> `useSearchSuggestions` keys a module-level LRU by normalized
 * query, and that cache is shared with the header — so a visitor who typed the query into the dropdown
 * and pressed Enter arrives here on a cache hit, with no second request. A visitor who arrived by URL
 * pays one debounced call.
 *
 * <p><b>Placement.</b> Rendered on both zero-match branches — `SearchPaginatedList`'s blending branch
 * and inside `SearchEmptyState`'s panel — between the verdict and the action block, giving the order
 * the wave-19 re-adjudication §1.1 adjudicated: **verdict → shelf → actions → filler rows**. It is not
 * a filler row and must never be counted as one; see the ordinal case in `SearchPaginatedList.spec.tsx`.
 *
 * <p><b>No key was minted.</b> The two headings are `search.tab.tags` and `search.tab.groups` — the tab
 * strip's own words for these things, which is the point: the shelf names its contents in the
 * vocabulary the page already uses. A chip's accessible name is `search.suggestions.browse_tag`
 * ("Browse {0}"), which shipped in all 25 catalogs with no consumer at all.
 */
export function SearchZeroMatchShelf(props: {
    query: string;
    localizer: Localizer;
    options?: SearchZeroMatchShelfOptions;
    className?: string;
}) {
    const { query, localizer, options, className } = props;
    const trimmedQuery = query.trim();

    // `enabled` gates fetching, not retention (see the hook). There is no dropdown here to open and
    // close, so it is simply "is there a query at all"; the hook's own minimum-length guard handles the
    // rest, and a blank query never reaches the network.
    const { status, response, responseQuery } = useSearchSuggestions(trimmedQuery, trimmedQuery.length > 0);

    const resolvedItems = React.useMemo<SearchSuggestionItem[]>(() => {
        // The dead-shelf gate, and the only place it lives. `responseQuery` is the normalized query the
        // response actually answers; anything else on screen would be a destination derived from a
        // query the visitor is no longer looking at.
        if (status !== "ready" || responseQuery === null || responseQuery !== trimmedQuery.toLowerCase()) {
            return [];
        }
        return buildSuggestionItems(response, localizer);
    }, [status, response, responseQuery, trimmedQuery, localizer]);

    const tagItems = React.useMemo(
        () => resolvedItems.filter((item) => item.kind === "tag").slice(0, MAX_SHELF_CHIPS),
        [resolvedItems],
    );
    const groupItems = React.useMemo(
        () => (options?.includeGroups
            ? resolvedItems.filter((item) => item.kind === "group").slice(0, MAX_SHELF_CHIPS)
            : []),
        [resolvedItems, options?.includeGroups],
    );

    if (tagItems.length === 0 && groupItems.length === 0) {
        // Nothing resolved, or nothing resolved YET. Rendering a heading over an empty row would be a
        // third claim on a page that already states the failure twice, and a skeleton here would
        // promise an answer the response may never contain.
        return null;
    }

    return (
        <div className={cn("space-y-3", className)} data-playwright-id="search-zero-match-shelf">
            {tagItems.length > 0 ? (
                <ShelfRow
                    kind="tags"
                    heading={localizer("search.tab.tags")}
                    icon={Hash}
                    items={tagItems}
                    localizer={localizer}
                />
            ) : null}
            {groupItems.length > 0 ? (
                <ShelfRow
                    kind="groups"
                    heading={localizer("search.tab.groups")}
                    icon={Users}
                    items={groupItems}
                    localizer={localizer}
                />
            ) : null}
        </div>
    );
}

function ShelfRow(props: {
    kind: "tags" | "groups";
    heading: string;
    icon: typeof Hash;
    items: SearchSuggestionItem[];
    localizer: Localizer;
}) {
    const { kind, heading, icon: Icon, items, localizer } = props;
    const headingId = `search-zero-match-shelf-${kind}`;

    return (
        <div data-playwright-id={`search-zero-match-shelf-${kind}`}>
            <p
                id={headingId}
                className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
                <Icon className="size-3.5 shrink-0" aria-hidden="true" />
                {heading}
            </p>
            {/* `role="list"` on a `flex` container: the browser drops implicit list semantics from a
                `<ul>` whose `display` is not `list-item`-bearing, and these ARE a set — a screen-reader
                user is owed "3 items" before deciding whether the shelf is worth walking. */}
            <div
                role="list"
                aria-labelledby={headingId}
                className="flex flex-wrap items-center gap-2"
            >
                {items.map((item) => (
                    <div role="listitem" key={item.key}>
                        {/*
                          * Flat-X pill (rule 23): a border rather than a shadow, one hover wash, the
                          * shared focus ring. `[@media(pointer:coarse)]:min-h-11` is the tap-target
                          * floor in the shape `DISCOVERY-53` and `DISCOVERY-67` settled on — a floor on
                          * the BOX, never a centred 44px slug, because these chips wrap at 412 px and
                          * an overhanging slug would collide across the `gap-2`. Gated on
                          * `(pointer: coarse)`, never on `sm:`, or
                          * `scripts/check-tap-target-mechanisms.mjs` check C fails it.
                          */}
                        <a
                            href={item.href}
                            // The visible label is the bare `#topic` or the group's name. On its own,
                            // out of the heading's context, that is a poor accessible name for a link —
                            // hence the already-translated verb. `search.suggestions.browse_tag`
                            // ("Browse {0}") had shipped in all 25 catalogs with no call site.
                            aria-label={localizer("search.suggestions.browse_tag", item.label)}
                            className="inline-flex max-w-full touch-manipulation items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors [@media(pointer:coarse)]:min-h-11 hover:bg-background2/60"
                            data-playwright-id="search-zero-match-shelf-item"
                        >
                            {/* A tag name and a group name are both unbounded; `truncate` keeps one long
                                one from stretching the chip past the column on a phone. */}
                            <span className="truncate">{item.label}</span>
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default SearchZeroMatchShelf;
